/**
 * Itinerary Validation System
 *
 * Rule-based validation to catch logical issues in generated itineraries.
 * Works alongside LLM generation to ensure schedules make sense.
 *
 * Validation Rules:
 * 1. Time Conflicts - No two items at same time (unless same location = sequential)
 * 2. Transit Windows - No activities during flights/trains
 * 3. Geography - Validate travel times between locations
 * 4. Regional Meals - Meal times appropriate for region
 * 5. Duration Sanity - Realistic durations per activity type
 * 6. Operating Hours - Activities at appropriate times
 * 7. Day Coherence - Logical flow, handle red-eyes/early departures
 * 8. Travel Spacing - Sufficient gaps based on Google Maps transit times
 *
 * Auth bridge: call configureValidationApi(fn) to inject a platform-specific
 * API caller (e.g., one that adds Bearer tokens). Falls back to plain fetch.
 */

import { REGIONAL_MEAL_TIMES, COUNTRY_TO_MEAL_REGION as COUNTRY_TO_REGION, CITY_TO_MEAL_REGION, getRegionalMealTimes } from './regionalMealTimes.js';

// Auth bridge: platform-specific API caller (injected by web/mobile)
let _callApi = null;

/**
 * Configure a custom API caller for authenticated requests.
 * @param {Function} fn - async (url, options) => Response
 */
export function configureValidationApi(fn) {
  _callApi = fn;
}

// ============================================
// ACTIVITY DURATION EXPECTATIONS
// ============================================
const ACTIVITY_DURATIONS = {
  // Format: { min: minutes, max: minutes, typical: minutes }

  // Cultural & Educational
  museum: { min: 60, max: 240, typical: 120 },
  gallery: { min: 45, max: 180, typical: 90 },
  landmark: { min: 30, max: 120, typical: 60 },
  church: { min: 20, max: 90, typical: 45 },
  cathedral: { min: 30, max: 120, typical: 60 },
  temple: { min: 30, max: 90, typical: 45 },
  palace: { min: 60, max: 180, typical: 120 },
  castle: { min: 60, max: 180, typical: 120 },
  ruins: { min: 60, max: 240, typical: 120 },
  monument: { min: 15, max: 45, typical: 30 },

  // Outdoor & Nature
  park: { min: 30, max: 180, typical: 90 },
  garden: { min: 30, max: 120, typical: 60 },
  beach: { min: 60, max: 300, typical: 180 },
  hike: { min: 60, max: 480, typical: 180 },
  trail: { min: 60, max: 360, typical: 150 },
  viewpoint: { min: 15, max: 60, typical: 30 },
  scenic_drive: { min: 60, max: 300, typical: 120 },
  nature_reserve: { min: 90, max: 300, typical: 180 },
  national_park: { min: 180, max: 480, typical: 300 },
  wildlife: { min: 120, max: 480, typical: 240 },
  safari: { min: 180, max: 480, typical: 300 },

  // Shopping & Markets
  shopping: { min: 30, max: 180, typical: 90 },
  market: { min: 30, max: 120, typical: 60 },
  souk: { min: 60, max: 180, typical: 90 },
  bazaar: { min: 60, max: 180, typical: 90 },

  // Entertainment & Activities
  tour: { min: 90, max: 480, typical: 180 },
  walking_tour: { min: 90, max: 180, typical: 120 },
  food_tour: { min: 120, max: 240, typical: 180 },
  cooking_class: { min: 120, max: 240, typical: 180 },
  show: { min: 60, max: 180, typical: 120 },
  concert: { min: 90, max: 180, typical: 120 },
  theater: { min: 90, max: 180, typical: 150 },
  cruise: { min: 60, max: 480, typical: 180 },
  boat_tour: { min: 60, max: 180, typical: 90 },

  // Wellness & Relaxation
  spa: { min: 60, max: 240, typical: 120 },
  massage: { min: 60, max: 120, typical: 90 },
  yoga: { min: 60, max: 90, typical: 75 },
  fitness: { min: 45, max: 90, typical: 60 },
  pool: { min: 60, max: 240, typical: 120 },

  // Meals - regional variations handled elsewhere, these are base durations
  breakfast: { min: 30, max: 90, typical: 45 },
  brunch: { min: 60, max: 120, typical: 90 },
  lunch: { min: 45, max: 120, typical: 60 },
  afternoon_tea: { min: 45, max: 90, typical: 60 }, // UK/Irish tradition - leisurely affair
  tea: { min: 45, max: 90, typical: 60 }, // Alias for afternoon_tea
  dinner: { min: 60, max: 180, typical: 90 },
  fine_dining: { min: 90, max: 180, typical: 120 },
  casual_dining: { min: 45, max: 90, typical: 60 },
  street_food: { min: 20, max: 45, typical: 30 },
  coffee: { min: 15, max: 45, typical: 30 },
  snack: { min: 15, max: 30, typical: 20 },

  // Transit/Travel (for validation, not user activities)
  flight: { min: 120, max: 960, typical: 300 },
  train: { min: 30, max: 480, typical: 120 },
  bus: { min: 30, max: 600, typical: 180 },
  ferry: { min: 30, max: 480, typical: 120 },
  drive: { min: 30, max: 600, typical: 180 },

  // Special activities
  theme_park: { min: 300, max: 720, typical: 480 },
  water_park: { min: 180, max: 360, typical: 240 },
  amusement_park: { min: 240, max: 480, typical: 360 },
  wine_tasting: { min: 60, max: 180, typical: 120 },
  brewery_tour: { min: 60, max: 120, typical: 90 },
  distillery_tour: { min: 60, max: 120, typical: 90 },
  farm_visit: { min: 60, max: 180, typical: 120 },
  factory_tour: { min: 60, max: 120, typical: 90 },

  // Default for unknown types
  default: { min: 30, max: 180, typical: 60 }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Parse time string to minutes since midnight
 */
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;

  // Handle formats: "9:00 AM", "14:30", "2:30 PM"
  const str = timeStr.toString().trim().toUpperCase();

  // Try 24-hour format first
  const match24 = str.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    return parseInt(match24[1]) * 60 + parseInt(match24[2]);
  }

  // Try 12-hour format
  const match12 = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (match12) {
    let hours = parseInt(match12[1]);
    const minutes = parseInt(match12[2]);
    const period = match12[3];

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  return null;
};

/**
 * Convert minutes to time string
 */
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
};

/**
 * Estimate airport transit time to city center based on destination
 * Returns minutes
 */
const estimateAirportTransit = (destination) => {
  if (!destination) return 45; // Default 45 minutes

  const lower = destination.toLowerCase();

  // Cities with long airport transit (60-90 minutes)
  const longTransit = ['tokyo', 'narita', 'haneda', 'london', 'heathrow', 'gatwick', 'new york', 'jfk', 'lga', 'ewr',
    'paris', 'cdg', 'orly', 'seoul', 'incheon', 'bangkok', 'suvarnabhumi', 'beijing', 'shanghai', 'pudong',
    'los angeles', 'lax', 'rome', 'fiumicino', 'mumbai', 'delhi', 'jakarta', 'manila', 'sao paulo'];

  // Cities with moderate transit (30-45 minutes)
  const moderateTransit = ['barcelona', 'madrid', 'amsterdam', 'dubai', 'hong kong', 'singapore', 'miami',
    'san francisco', 'chicago', 'berlin', 'munich', 'vienna', 'prague', 'lisbon', 'sydney', 'melbourne',
    'toronto', 'vancouver', 'boston', 'seattle', 'denver', 'atlanta'];

  // Cities with fast transit (15-25 minutes - city airports or excellent rail)
  const fastTransit = ['zurich', 'geneva', 'copenhagen', 'oslo', 'stockholm', 'helsinki', 'dublin',
    'edinburgh', 'florence', 'nice', 'malaga', 'san diego', 'portland'];

  for (const city of longTransit) {
    if (lower.includes(city)) return 75;
  }
  for (const city of moderateTransit) {
    if (lower.includes(city)) return 45;
  }
  for (const city of fastTransit) {
    if (lower.includes(city)) return 25;
  }

  return 45; // Default moderate transit
};

/**
 * Get regional meal times with async AI fallback for unknown destinations
 * @param {string} destination - The destination to look up
 * @param {string} backendUrl - Backend URL for API calls (optional)
 * @returns {Promise<Object>} Meal times object with breakfast, lunch, dinner arrays
 */
const getRegionalMealTimesAsync = async (destination, backendUrl = '') => {
  if (!destination) return REGIONAL_MEAL_TIMES.default;

  // First try sync lookup (instant, from static maps)
  const syncResult = getRegionalMealTimes(destination);

  // Check if we got a specific regional result (not just default)
  const lower = destination.toLowerCase();
  const hasSpecificMatch =
    Object.keys(COUNTRY_TO_REGION).some(c => lower.includes(c)) ||
    Object.keys(CITY_TO_MEAL_REGION).some(c => lower.includes(c));

  if (hasSpecificMatch) {
    return { ...syncResult, source: 'static' };
  }

  // Unknown destination - try AI lookup
  try {
    const apiUrl = `${backendUrl}/api/regional-meal-times`;
    const apiOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination })
    };
    const response = await (_callApi || fetch)(apiUrl, apiOptions);

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        return {
          breakfast: data.breakfast,
          lunch: data.lunch,
          dinner: data.dinner,
          notes: data.notes,
          source: 'ai'
        };
      }
    }
  } catch (error) {
    console.warn('Regional meal times API failed:', error.message);
  }

  // Fallback to default
  return { ...REGIONAL_MEAL_TIMES.default, source: 'default' };
};

/**
 * Check if two locations are the same (for sequential scheduling)
 */
const isSameLocation = (item1, item2) => {
  if (!item1 || !item2) return false;

  // Compare addresses if available
  const addr1 = (item1.address || item1.location || '').toLowerCase().trim();
  const addr2 = (item2.address || item2.location || '').toLowerCase().trim();

  if (addr1 && addr2 && addr1 === addr2) return true;

  // Compare venue names
  const name1 = (item1.name || item1.activity || item1.restaurant || '').toLowerCase().trim();
  const name2 = (item2.name || item2.activity || item2.restaurant || '').toLowerCase().trim();

  if (name1 && name2 && name1 === name2) return true;

  // Check if one contains the other (e.g., "Louvre Museum" and "Louvre")
  if (name1 && name2 && (name1.includes(name2) || name2.includes(name1))) return true;

  return false;
};

/**
 * Detect activity type from item
 */
const detectActivityType = (item) => {
  const name = (item.name || item.activity || item.restaurant || '').toLowerCase();
  const category = (item.category || item.type || '').toLowerCase();

  // Check category first
  if (category) {
    if (category.includes('museum')) return 'museum';
    if (category.includes('gallery')) return 'gallery';
    if (category.includes('park')) return 'park';
    if (category.includes('beach')) return 'beach';
    if (category.includes('shopping')) return 'shopping';
    if (category.includes('market')) return 'market';
    if (category.includes('tour')) return 'tour';
    if (category.includes('spa')) return 'spa';
  }

  // Check name keywords
  if (name.includes('museum')) return 'museum';
  if (name.includes('gallery') || name.includes('art ')) return 'gallery';
  if (name.includes('park') || name.includes('garden')) return 'park';
  if (name.includes('beach') || name.includes('playa')) return 'beach';
  if (name.includes('mall') || name.includes('shopping')) return 'shopping';
  if (name.includes('market') || name.includes('mercado') || name.includes('marché')) return 'market';
  if (name.includes('tour') || name.includes('walk')) return 'tour';
  if (name.includes('spa') || name.includes('massage')) return 'spa';
  if (name.includes('hike') || name.includes('trail')) return 'hike';
  if (name.includes('show') || name.includes('theater') || name.includes('theatre')) return 'show';

  return 'default';
};

/**
 * Detect meal type from item
 */
const detectMealType = (item) => {
  const mealType = (item.meal || item.mealType || '').toLowerCase();
  const name = (item.restaurant || item.name || '').toLowerCase();

  if (mealType.includes('breakfast') || name.includes('breakfast')) return 'breakfast';
  if (mealType.includes('brunch') || name.includes('brunch')) return 'brunch';
  if (mealType.includes('lunch') || name.includes('lunch')) return 'lunch';
  if (mealType.includes('dinner') || name.includes('dinner')) return 'dinner';
  if (mealType.includes('coffee') || name.includes('coffee') || name.includes('café')) return 'coffee';
  if (mealType.includes('snack')) return 'snack';

  return null;
};

// ============================================
// VALIDATION RULES
// ============================================

/**
 * Rule 1: Time Conflict Detection
 * Check for items at the same time (unless same location = sequential)
 */
const checkTimeConflicts = (day) => {
  const issues = [];
  const items = [];

  // Collect all scheduled items
  if (day.schedule) {
    day.schedule.forEach((item, idx) => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) {
        items.push({ ...item, time, itemType: 'activity', originalIndex: idx });
      }
    });
  }

  if (day.meals) {
    day.meals.forEach((item, idx) => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) {
        items.push({ ...item, time, itemType: 'meal', originalIndex: idx });
      }
    });
  }

  // Sort by time
  items.sort((a, b) => a.time - b.time);

  // Check for conflicts
  for (let i = 0; i < items.length - 1; i++) {
    const current = items[i];
    const next = items[i + 1];

    // Allow if same location (sequential scheduling)
    if (isSameLocation(current, next)) {
      continue;
    }

    // Calculate current item's end time
    let currentDuration = 60; // Default 1 hour
    if (current.duration) {
      const durationMatch = String(current.duration).match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)?/i);
      if (durationMatch) {
        const value = parseFloat(durationMatch[1]);
        const unit = (durationMatch[2] || '').toLowerCase();
        if (unit.startsWith('hour') || unit === 'hr') {
          currentDuration = value * 60;
        } else if (unit.startsWith('min')) {
          currentDuration = value;
        } else {
          // No unit specified — bare number: ≤12 → hours, >12 → minutes
          currentDuration = value > 12 ? value : value * 60;
        }
      }
    }
    const currentEndTime = current.time + currentDuration;

    // Same start time = conflict
    if (current.time === next.time) {
      issues.push({
        type: 'time_conflict',
        severity: 'error',
        message: `Time conflict: "${current.activity || current.restaurant}" and "${next.activity || next.restaurant}" both scheduled at ${minutesToTimeString(current.time)}`,
        items: [current, next],
        autoFixable: true,
        fix: { action: 'shift_later', item: next, newTime: current.time + currentDuration + 15 }
      });
    }
    // Overlapping duration = long activity with meal inside → cap activity, don't shift meal
    else if (currentEndTime > next.time && currentDuration > 150 && next.itemType === 'meal') {
      const newDuration = next.time - current.time - 15; // End 15 min before meal
      if (newDuration >= 30) {
        issues.push({
          type: 'long_activity_meal_overlap',
          severity: 'warning',
          message: `"${current.activity || current.restaurant}" (${currentDuration} min) overlaps with meal "${next.activity || next.restaurant}" at ${minutesToTimeString(next.time)} — capping activity duration`,
          items: [current, next],
          autoFixable: true,
          fix: { action: 'adjust_duration', item: { ...current, itemType: current.itemType }, newDuration, originalIndex: current.originalIndex }
        });
      }
    }
    // Overlapping duration = conflict (current activity ends AFTER next starts)
    else if (currentEndTime > next.time) {
      const overlapMinutes = currentEndTime - next.time;
      issues.push({
        type: 'time_overlap',
        severity: 'error',
        message: `Time overlap: "${current.activity || current.restaurant}" (ends at ${minutesToTimeString(currentEndTime)}) overlaps with "${next.activity || next.restaurant}" (starts at ${minutesToTimeString(next.time)}) by ${overlapMinutes} minutes`,
        items: [current, next],
        autoFixable: true,
        fix: { action: 'shift_later', item: next, newTime: currentEndTime + 15 }
      });
    }
  }

  return issues;
};

/**
 * Rule 2: Transit Window Blocking
 * Check for activities scheduled during inter-city travel
 */
const checkTransitWindows = (day, context = {}) => {
  const issues = [];

  // Check if this is a travel day
  const travelInfo = context.travelInfo || day.travelInfo;
  if (!travelInfo || !travelInfo.isTransitDay) return issues;

  // Get transit window
  const departureTime = parseTimeToMinutes(travelInfo.departureTime);
  const arrivalTime = parseTimeToMinutes(travelInfo.arrivalTime);

  if (departureTime === null || arrivalTime === null) return issues;

  // Add buffers: 2-3 hours before departure, 2-3 hours after arrival
  const bufferBefore = travelInfo.transportType === 'flight' ? 180 : 60; // 3hr for flights, 1hr for trains
  const bufferAfter = travelInfo.transportType === 'flight' ? 120 : 60; // 2hr for flights, 1hr for trains

  const blockedStart = departureTime - bufferBefore;
  const blockedEnd = arrivalTime + bufferAfter;

  // Check activities
  const checkItem = (item, type) => {
    const itemTime = parseTimeToMinutes(item.time);
    if (itemTime === null) return;

    const duration = item.duration || 60;
    const itemEnd = itemTime + duration;

    // Check if item overlaps with blocked window
    if (itemTime < blockedEnd && itemEnd > blockedStart) {
      // If it's during actual transit (not just buffer), it's an error
      if (itemTime >= departureTime && itemTime < arrivalTime) {
        issues.push({
          type: 'activity_during_transit',
          severity: 'error',
          message: `"${item.activity || item.restaurant}" scheduled at ${minutesToTimeString(itemTime)} during ${travelInfo.transportType || 'transit'} (${minutesToTimeString(departureTime)} - ${minutesToTimeString(arrivalTime)})`,
          item,
          autoFixable: false,
          needsRegeneration: true
        });
      } else {
        // In buffer zone - warning
        issues.push({
          type: 'activity_in_transit_buffer',
          severity: 'warning',
          message: `"${item.activity || item.restaurant}" at ${minutesToTimeString(itemTime)} may conflict with travel buffer`,
          item,
          autoFixable: true,
          fix: itemTime < departureTime
            ? { action: 'shift_earlier', item, newTime: blockedStart - duration - 30 }
            : { action: 'shift_later', item, newTime: blockedEnd + 30 }
        });
      }
    }
  };

  if (day.schedule) day.schedule.forEach(item => checkItem(item, 'activity'));
  if (day.meals) day.meals.forEach(item => checkItem(item, 'meal'));

  return issues;
};

/**
 * Rule 4: Regional Meal Times
 * Check if meals are at appropriate times for the region
 */
const checkMealTimes = (day, destination) => {
  const issues = [];
  const mealTimes = getRegionalMealTimes(destination);

  if (!day.meals) return issues;

  day.meals.forEach((meal, idx) => {
    const mealType = detectMealType(meal);
    if (!mealType || !mealTimes[mealType]) return;

    const itemTime = parseTimeToMinutes(meal.time);
    if (itemTime === null) return;

    const [startHour, endHour] = mealTimes[mealType];
    const windowStart = startHour * 60;
    const windowEnd = endHour * 60;

    if (itemTime < windowStart || itemTime > windowEnd) {
      // Calculate how far outside the window the meal is
      const hoursBeforeWindow = (windowStart - itemTime) / 60;
      const hoursAfterWindow = (itemTime - windowEnd) / 60;
      const hoursOutside = Math.max(hoursBeforeWindow, hoursAfterWindow, 0);

      // Any meal outside the window is at least a warning; 2+ hours outside is an error
      const severity = hoursOutside >= 2 ? 'error' : 'warning';

      issues.push({
        type: 'meal_time_mismatch',
        severity,
        message: `${mealType.charAt(0).toUpperCase() + mealType.slice(1)} at ${minutesToTimeString(itemTime)} is outside typical ${destination || 'local'} ${mealType} hours (${startHour}:00-${endHour}:00)`,
        item: meal,
        autoFixable: true,
        fix: {
          action: 'shift_time',
          item: { ...meal, itemType: 'meal' },
          newTime: itemTime < windowStart ? windowStart : windowEnd - 60,
          originalIndex: idx
        }
      });
    }
  });

  return issues;
};

/**
 * Rule 5: Duration Sanity Checks
 * Check if activity durations are realistic
 */
const checkDurationSanity = (day) => {
  const issues = [];

  if (!day.schedule) return issues;

  day.schedule.forEach((item, idx) => {
    const activityType = detectActivityType(item);
    const expected = ACTIVITY_DURATIONS[activityType] || ACTIVITY_DURATIONS.default;
    const duration = item.duration || expected.typical;

    if (duration < expected.min) {
      issues.push({
        type: 'duration_too_short',
        severity: 'warning',
        message: `"${item.activity}" duration (${duration} min) seems too short for a ${activityType} (min: ${expected.min} min)`,
        item,
        autoFixable: true,
        fix: { action: 'adjust_duration', item, newDuration: expected.typical, originalIndex: idx }
      });
    } else if (duration > expected.max) {
      issues.push({
        type: 'duration_too_long',
        severity: 'info',
        message: `"${item.activity}" duration (${duration} min) is longer than typical for a ${activityType} (max: ${expected.max} min)`,
        item,
        autoFixable: false // Don't auto-fix long durations, user may want extended visit
      });
    }
  });

  return issues;
};

/**
 * Rule 7: Day Coherence
 * Check for logical day flow, handle red-eyes/early departures
 */
const checkDayCoherence = (day, context = {}) => {
  const issues = [];
  const items = [];

  // Collect all items with times
  if (day.schedule) {
    day.schedule.forEach(item => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) items.push({ ...item, time, type: 'activity' });
    });
  }
  if (day.meals) {
    day.meals.forEach(item => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) items.push({ ...item, time, type: 'meal' });
    });
  }

  if (items.length === 0) return issues;

  items.sort((a, b) => a.time - b.time);

  const firstItem = items[0];
  const lastItem = items[items.length - 1];

  // Check for red-eye arrival (context should have arrival info)
  if (context.arrivalTime) {
    const arrivalMins = parseTimeToMinutes(context.arrivalTime);
    if (arrivalMins !== null) {
      // Red-eye: arrival before 8am
      const isRedEye = arrivalMins < 8 * 60;

      // Base buffer: customs/baggage (30-60 min) + transit to city (30-90 min depending on city)
      // Use airport transit estimate if provided, otherwise estimate based on destination type
      const airportTransitMins = context.airportTransitMinutes || estimateAirportTransit(context.destination);
      const baseBuffer = 45 + airportTransitMins; // 45 min for customs/baggage + transit
      const minFirstActivity = arrivalMins + (isRedEye ? Math.max(180, baseBuffer + 30) : Math.max(120, baseBuffer));

      if (firstItem.time < minFirstActivity) {
        const bufferHours = Math.round((minFirstActivity - arrivalMins) / 60 * 10) / 10;
        issues.push({
          type: 'too_early_after_arrival',
          severity: 'warning',
          message: `First activity "${firstItem.activity || firstItem.restaurant}" at ${minutesToTimeString(firstItem.time)} is too soon after arrival at ${context.arrivalTime}. Allow ~${bufferHours}hrs for customs and transit to city.`,
          item: firstItem,
          autoFixable: true,
          fix: { action: 'shift_later', item: firstItem, newTime: minFirstActivity }
        });
      }
    }
  }

  // Check for early/night departure
  if (context.departureTime) {
    const departureMins = parseTimeToMinutes(context.departureTime);
    if (departureMins !== null) {
      // Night departure (after 8pm) - day can end later
      // Early departure (before 10am) - day should end by 6pm previous day? (handled by transit)
      // Daytime departure - last activity should end 3hr before
      const isNightDeparture = departureMins >= 20 * 60;
      const maxLastActivity = isNightDeparture
        ? departureMins - 180 // 3hr before night departure
        : departureMins - 180; // 3hr before any departure

      const lastItemEnd = lastItem.time + (lastItem.duration || 60);

      if (lastItemEnd > maxLastActivity) {
        issues.push({
          type: 'too_late_before_departure',
          severity: 'warning',
          message: `Last activity "${lastItem.activity || lastItem.restaurant}" ends at ${minutesToTimeString(lastItemEnd)}, too close to departure at ${context.departureTime}. ${isNightDeparture ? 'For night departures, ' : ''}Allow 3 hours buffer.`,
          item: lastItem,
          autoFixable: true,
          fix: { action: 'shift_earlier', item: lastItem, newTime: maxLastActivity - (lastItem.duration || 60) }
        });
      }
    }
  }

  // Fix 4: Check checkout/breakfast ordering on transition days
  const checkoutItem = items.find(i =>
    (i.activity || '').toLowerCase().match(/check.?out|checkout/)
  );
  const breakfastItem = items.find(i =>
    i.type === 'meal' && (i.restaurant || i.meal || i.activity || '').toLowerCase().includes('breakfast')
  );
  if (checkoutItem && breakfastItem && breakfastItem.time > checkoutItem.time) {
    // Breakfast is after checkout — swap: move breakfast to 30 min before checkout
    const newBreakfastTime = checkoutItem.time - 75; // 1hr15min before checkout for a 45-min breakfast
    if (newBreakfastTime >= 6 * 60) { // Not before 6 AM
      issues.push({
        type: 'breakfast_after_checkout',
        severity: 'error',
        message: `Breakfast at ${minutesToTimeString(breakfastItem.time)} is after checkout at ${minutesToTimeString(checkoutItem.time)} — moving breakfast earlier`,
        item: breakfastItem,
        autoFixable: true,
        fix: { action: 'shift_time', item: { ...breakfastItem, itemType: 'meal' }, newTime: newBreakfastTime }
      });
    }
  }

  // Fix 10: Pace-aware late-night cap
  const preferredPace = context.preferredPace || 'moderate';
  const paceEndTimes = {
    'relaxed': 21 * 60,       // 9 PM
    'moderate': 22 * 60,      // 10 PM
    'balanced': 22 * 60,      // 10 PM
    'packed': 23 * 60,        // 11 PM
    'adventurous': 23 * 60,   // 11 PM
    'high-energy': 23 * 60,   // 11 PM
  };
  const maxEndTime = paceEndTimes[preferredPace] || 22 * 60;

  // Check for unreasonably late end based on pace (unless nightlife)
  const isNightlifeDay = items.some(i =>
    (i.activity || '').toLowerCase().match(/club|bar|nightlife|pub|disco/)
  );

  if (!isNightlifeDay && lastItem.time > maxEndTime) {
    issues.push({
      type: 'day_ends_too_late',
      severity: preferredPace === 'relaxed' ? 'warning' : 'info',
      message: `Day ends at ${minutesToTimeString(lastItem.time)}, too late for ${preferredPace} pace (max ${minutesToTimeString(maxEndTime)})`,
      item: lastItem,
      autoFixable: true,
      fix: { action: 'shift_earlier', item: lastItem, newTime: maxEndTime - (lastItem.duration || 60) }
    });
  }

  return issues;
};

/**
 * Rule 8: Travel Spacing
 * Check if there's sufficient gap between activities based on location
 */
const checkTravelSpacing = (day, cachedTransitTimes = {}) => {
  const issues = [];
  const items = [];

  // Collect items with times and locations
  if (day.schedule) {
    day.schedule.forEach((item, idx) => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) {
        items.push({
          ...item,
          time,
          duration: item.duration || 60,
          itemType: 'activity',
          originalIndex: idx
        });
      }
    });
  }
  if (day.meals) {
    day.meals.forEach((item, idx) => {
      const time = parseTimeToMinutes(item.time);
      if (time !== null) {
        items.push({
          ...item,
          time,
          duration: item.duration || 60,
          itemType: 'meal',
          originalIndex: idx
        });
      }
    });
  }

  items.sort((a, b) => a.time - b.time);

  for (let i = 0; i < items.length - 1; i++) {
    const current = items[i];
    const next = items[i + 1];

    const currentEnd = current.time + current.duration;
    const gap = next.time - currentEnd;

    // Skip if same location
    if (isSameLocation(current, next)) continue;

    // Check cached transit time if available
    const cacheKey = `${current.address || current.location}|${next.address || next.location}`;
    const transitTime = cachedTransitTimes[cacheKey];

    if (transitTime && gap < transitTime) {
      issues.push({
        type: 'insufficient_travel_time',
        severity: 'warning',
        message: `Only ${gap} min between "${current.activity || current.restaurant}" and "${next.activity || next.restaurant}", but transit takes ~${transitTime} min`,
        items: [current, next],
        autoFixable: true,
        fix: { action: 'shift_later', item: next, newTime: currentEnd + transitTime + 15 }
      });
    } else if (!transitTime && gap < 15) {
      // No cached data, use minimum buffer
      issues.push({
        type: 'no_travel_buffer',
        severity: 'info',
        message: `Only ${gap} min gap between activities - may need more travel time`,
        items: [current, next],
        autoFixable: true,
        fix: { action: 'shift_later', item: next, newTime: currentEnd + 20 }
      });
    }
  }

  return issues;
};

// ============================================
// MAIN VALIDATION FUNCTION
// ============================================

/**
 * Validate a single day's itinerary
 * @param {Object} day - The day object with schedule and meals
 * @param {Object} context - Additional context (destination, travelInfo, arrivalTime, etc.)
 * @returns {Object} - { issues: [], fixes: [], needsRegeneration: boolean }
 */
export const validateDay = (day, context = {}) => {
  const allIssues = [];

  // Run all validators
  allIssues.push(...checkTimeConflicts(day));
  allIssues.push(...checkTransitWindows(day, context));
  allIssues.push(...checkMealTimes(day, context.destination));
  allIssues.push(...checkDurationSanity(day));
  allIssues.push(...checkDayCoherence(day, context));
  allIssues.push(...checkTravelSpacing(day, context.cachedTransitTimes || {}));

  // Separate auto-fixable from requiring regeneration
  const autoFixable = allIssues.filter(i => i.autoFixable);
  const needsRegeneration = allIssues.some(i => i.needsRegeneration);
  const errors = allIssues.filter(i => i.severity === 'error');
  const warnings = allIssues.filter(i => i.severity === 'warning');

  return {
    issues: allIssues,
    errors,
    warnings,
    autoFixable,
    needsRegeneration,
    isValid: errors.length === 0 && !needsRegeneration
  };
};

/**
 * Apply auto-fixes to a day
 * @param {Object} day - The day object
 * @param {Array} fixes - Array of fix objects
 * @returns {Object} - Fixed day object
 */
export const applyFixes = (day, fixes) => {
  const fixed = JSON.parse(JSON.stringify(day)); // Deep clone

  for (const fix of fixes) {
    if (!fix.fix) continue;

    const { action, item, newTime, newDuration, originalIndex } = fix.fix;

    if (action === 'shift_later' || action === 'shift_earlier' || action === 'shift_time') {
      // Find and update the item
      if (item.itemType === 'activity' && fixed.schedule) {
        const idx = originalIndex ?? fixed.schedule.findIndex(i =>
          (i.activity || i.name) === (item.activity || item.name)
        );
        if (idx >= 0) {
          fixed.schedule[idx].time = minutesToTimeString(newTime);
        }
      } else if (item.itemType === 'meal' && fixed.meals) {
        const idx = originalIndex ?? fixed.meals.findIndex(i =>
          (i.restaurant || i.name) === (item.restaurant || item.name)
        );
        if (idx >= 0) {
          fixed.meals[idx].time = minutesToTimeString(newTime);
        }
      }
    }

    if (action === 'adjust_duration' && fixed.schedule) {
      const idx = originalIndex ?? fixed.schedule.findIndex(i =>
        (i.activity || i.name) === (item.activity || item.name)
      );
      if (idx >= 0) {
        fixed.schedule[idx].duration = newDuration;
      }
    }
  }

  return fixed;
};

/**
 * Validate and auto-fix a day
 * @param {Object} day - The day object
 * @param {Object} context - Validation context
 * @returns {Object} - { day: fixedDay, validation: validationResult, wasFixed: boolean }
 */
export const validateAndFixDay = (day, context = {}) => {
  const validation = validateDay(day, context);

  if (validation.isValid) {
    return { day, validation, wasFixed: false };
  }

  if (validation.needsRegeneration) {
    return { day, validation, wasFixed: false, needsRegeneration: true };
  }

  // Apply auto-fixes
  const fixedDay = applyFixes(day, validation.autoFixable);

  // Re-validate after fixes
  const revalidation = validateDay(fixedDay, context);

  return {
    day: fixedDay,
    validation: revalidation,
    wasFixed: true,
    appliedFixes: validation.autoFixable.length
  };
};

// ============================================
// PROMPT HELPER - Rules for LLM
// ============================================

/**
 * Get scheduling rules to include in generation prompt
 */
export const getSchedulingRulesForPrompt = (context = {}) => {
  const { destination, arrivalTime, departureTime, isRedEye } = context;
  const mealTimes = getRegionalMealTimes(destination);

  const rules = [
    'SCHEDULING RULES (MUST FOLLOW):',
    '1. No two activities can start at the same time unless at the same venue',
    '2. Allow minimum travel buffer between different locations:',
    '   - Same neighborhood: 15-20 min',
    '   - Different neighborhoods: 30-45 min',
    '   - Different cities: Use actual transit time',
    '3. On travel days (flights/trains):',
    '   - No activities during transit window',
    `   - First activity after arrival: minimum ${isRedEye ? '3' : '2-3'} hours buffer`,
    '   - Last activity before departure: end 3 hours before',
  ];

  // Add regional meal times
  rules.push(`4. Meal times for ${destination || 'this region'}:`);
  rules.push(`   - Breakfast: ${mealTimes.breakfast[0]}:00-${mealTimes.breakfast[1]}:00`);
  rules.push(`   - Lunch: ${mealTimes.lunch[0]}:00-${mealTimes.lunch[1]}:00`);
  rules.push(`   - Dinner: ${mealTimes.dinner[0]}:00-${mealTimes.dinner[1]}:00`);

  rules.push('5. Activity durations:');
  rules.push('   - Museums: 1.5-3 hours');
  rules.push('   - Restaurants: 1-2 hours');
  rules.push('   - Coffee/snack: 30 min');
  rules.push('   - Major attractions: 2-4 hours');

  if (arrivalTime) {
    const arrivalMins = parseTimeToMinutes(arrivalTime);
    const isRedEyeArrival = arrivalMins && arrivalMins < 8 * 60;
    rules.push(`6. Arrival at ${arrivalTime}${isRedEyeArrival ? ' (red-eye)' : ''}: First activity no earlier than ${isRedEyeArrival ? '10:00 AM' : '2-3 hours after arrival'}`);
  }

  if (departureTime) {
    const departureMins = parseTimeToMinutes(departureTime);
    const isNightDeparture = departureMins && departureMins >= 20 * 60;
    rules.push(`7. Departure at ${departureTime}${isNightDeparture ? ' (night departure)' : ''}: Day ends 3 hours before departure`);
  }

  return rules.join('\n');
};

// Export individual validators for testing
export {
  checkTimeConflicts,
  checkTransitWindows,
  checkMealTimes,
  checkDurationSanity,
  checkDayCoherence,
  checkTravelSpacing,
  getRegionalMealTimes,
  getRegionalMealTimesAsync,
  parseTimeToMinutes,
  minutesToTimeString,
  REGIONAL_MEAL_TIMES,
  ACTIVITY_DURATIONS
};
